/**
 * ActiveRemoteSession
 *
 * Floating chat panel visible to the end user (remote machine user) while an
 * ICT engineer is conducting a remote support session on their machine.
 *
 * How the user receives messages:
 *   - NOT through the agent (the agent is a headless tray app with no chat UI)
 *   - The user's ICTops browser tab receives messages via the root Socket.io
 *     connection (JWT socket every logged-in user has open).
 *   - The server pushes a 'remote:chat' notification to the user's socket when
 *     the engineer sends a message.
 *   - If the browser tab is in the background / minimised, a browser OS-level
 *     notification (Web Notifications API) is shown so the user is alerted
 *     even when they're not looking at the ICTops tab.
 *   - An audio chime plays on each incoming message.
 *
 * The component:
 *   1. Polls once on mount to check if the user has an active remote session
 *   2. Listens on the root Socket.io connection for 'remote:chat' events
 *   3. Requests browser notification permission on session start
 *   4. Shows OS notification + plays chime when a message arrives while the
 *      tab is hidden or the panel is collapsed
 *   5. Renders a compact floating panel at the bottom-right of the screen
 *   6. Allows the user to reply via the REST API
 *
 * Shown only to non-ICT-staff users (engineers use the full RemoteDesktopViewer).
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Paper, Typography, TextField, IconButton, Avatar,
  Chip, Collapse, Tooltip
} from '@mui/material';
import {
  Send, SupportAgent, ExpandMore, ExpandLess, Circle
} from '@mui/icons-material';
import api    from '../../api/axios';
import socket from '../../socket';
import { useAuth } from '../../context/AuthContext';

const ICT_STAFF_ROLES = ['admin', 'ict_manager', 'sr_engineer', 'jr_engineer'];

// ── Notification permission ───────────────────────────────────────────────────
async function requestNotificationPermission() {
  if (!('Notification' in window)) return 'denied';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied')  return 'denied';
  const result = await Notification.requestPermission();
  return result;
}

function showBrowserNotification(title, body, onClick) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  const notif = new Notification(title, {
    body,
    icon:   '/favicon.ico',
    badge:  '/favicon.ico',
    tag:    'ictops-remote-chat', // replace existing so they don't stack
    silent: false,
  });
  notif.onclick = () => {
    window.focus();
    if (onClick) onClick();
    notif.close();
  };
  // Auto-close after 6 seconds
  setTimeout(() => notif.close(), 6000);
}

// ── Chime (short 440 Hz beep using Web Audio API) ────────────────────────────
function playChime() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    gain.gain.setValueAtTime(0.2, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.4);
    osc.onended = () => ctx.close();
  } catch {
    // Web Audio not available — silent fallback
  }
}

// ─────────────────────────────────────────────────────────────────────────────

export default function ActiveRemoteSession() {
  const { user } = useAuth();

  const [session,    setSession]    = useState(null);
  const [messages,   setMessages]   = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [open,       setOpen]       = useState(true);
  const [sending,    setSending]    = useState(false);
  const [unread,     setUnread]     = useState(0);
  const messagesEndRef = useRef(null);
  const notifPermRef   = useRef('default'); // track permission without re-render

  const isStaff = ICT_STAFF_ROLES.includes(user?.role);

  // ── Check for active session on mount ────────────────────────────────────
  const fetchActiveSession = useCallback(async () => {
    if (!user || isStaff) return;
    try {
      const res = await api.get('/remote-desktop/sessions/history', {
        params: { limit: 5, skip: 0 }
      });
      const sessions = res.data?.data || [];
      const active   = sessions.find(s => s.status === 'active');
      if (active) {
        setSession(active);
        const msgRes = await api.get(`/remote-desktop/session/${active._id}/messages`, {
          params: { limit: 50 }
        });
        setMessages(msgRes.data?.data || []);
      } else {
        setSession(null);
        setMessages([]);
      }
    } catch {
      // Silent — never break the page if remote desktop API is unavailable
    }
  }, [user, isStaff]);

  useEffect(() => {
    fetchActiveSession();
  }, [fetchActiveSession]);

  // ── Request browser notification permission when session starts ───────────
  useEffect(() => {
    if (!session) return;
    requestNotificationPermission().then(p => {
      notifPermRef.current = p;
    });
  }, [session?._id]);

  // ── Listen for real-time chat messages from engineer ─────────────────────
  useEffect(() => {
    if (!user || isStaff) return;

    const onNotification = (data) => {
      // ── New message ───────────────────────────────────────────────────────
      if (data.type === 'remote:chat') {
        // No session yet — reload state to pick it up
        if (!session && data.sessionId) {
          fetchActiveSession();
          return;
        }
        if (data.sessionId !== session?._id) return;

        const newMsg = {
          _id:        data.messageId || `tmp-${Date.now()}`,
          senderId:   { name: data.senderName },
          senderRole: data.senderRole || 'engineer',
          message:    data.message,
          timestamp:  data.timestamp,
        };

        setMessages(prev => [...prev, newMsg]);

        // Alert when panel is collapsed or tab is not focused
        const tabHidden  = document.hidden;
        const panelClosed = !open;

        if (tabHidden || panelClosed) {
          // Increment unread badge
          setUnread(prev => prev + 1);

          // OS-level browser notification (works even when tab is minimised)
          showBrowserNotification(
            `Message from ${data.senderName || 'Support Engineer'}`,
            data.message,
            () => setOpen(true)
          );

          // Audible chime
          playChime();
        }
      }

      // ── Session ended ─────────────────────────────────────────────────────
      if (data.type === 'remote:session_ended' && data.sessionId === session?._id) {
        setSession(null);
        setMessages([]);
        setUnread(0);
      }
    };

    socket.on('notification', onNotification);
    return () => socket.off('notification', onNotification);
  }, [user, isStaff, session, open, fetchActiveSession]);

  // Also play chime + show notification when tab becomes visible and there are unread messages
  useEffect(() => {
    const onVisibilityChange = () => {
      // When the tab comes back into view, if panel is open, clear badge
      if (!document.hidden && open) setUnread(0);
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [open]);

  // ── Auto-scroll to latest message ────────────────────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Clear unread badge when panel is opened
  useEffect(() => {
    if (open) setUnread(0);
  }, [open]);

  // ── Send message ──────────────────────────────────────────────────────────
  const handleSend = async () => {
    if (!newMessage.trim() || !session?._id || sending) return;
    const text = newMessage.trim();
    setNewMessage('');
    setSending(true);

    // Optimistic update
    setMessages(prev => [...prev, {
      _id:        `tmp-${Date.now()}`,
      senderId:   { name: user.name },
      senderRole: 'remote_user',
      message:    text,
      timestamp:  new Date(),
    }]);

    try {
      await api.post(`/remote-desktop/session/${session._id}/message`, { message: text });
    } catch (err) {
      console.error('[ActiveRemoteSession] Failed to send message:', err.message);
    } finally {
      setSending(false);
    }
  };

  // Don't render if staff or no active session
  if (isStaff || !session) return null;

  const engineerName = session.engineerId?.name || 'Support Engineer';

  return (
    <Box
      sx={{
        position:  'fixed',
        bottom:    24,
        right:     24,
        zIndex:    1400,
        width:     340,
        maxWidth:  'calc(100vw - 48px)',
        boxShadow: 6,
        borderRadius: 3,
        overflow: 'hidden',
      }}
    >
      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>

        {/* ── Header (click to expand/collapse) ─────────────────────────── */}
        <Box
          sx={{
            display:    'flex',
            alignItems: 'center',
            gap:        1,
            p:          1.5,
            bgcolor:    'primary.main',
            color:      'white',
            cursor:     'pointer',
            userSelect: 'none',
          }}
          onClick={() => setOpen(o => !o)}
        >
          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.dark' }}>
            <SupportAgent sx={{ fontSize: 18 }} />
          </Avatar>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={700} noWrap>
              Remote Support — {engineerName}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Circle sx={{ fontSize: 8, color: '#69f0ae' }} />
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Session active
              </Typography>
            </Box>
          </Box>

          {/* Unread badge — visible only when collapsed */}
          {unread > 0 && !open && (
            <Chip
              label={unread}
              size="small"
              sx={{
                bgcolor: '#ff5252', color: 'white',
                fontWeight: 700, height: 20, fontSize: 11,
                animation: 'pulse 1s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%, 100%': { opacity: 1 },
                  '50%':      { opacity: 0.6 },
                },
              }}
            />
          )}

          <Tooltip title={open ? 'Minimise' : 'Open chat'}>
            <IconButton size="small" sx={{ color: 'white' }}>
              {open ? <ExpandMore fontSize="small" /> : <ExpandLess fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>

        {/* ── Chat body ──────────────────────────────────────────────────── */}
        <Collapse in={open}>
          {/* Info banner */}
          <Box sx={{ px: 2, py: 1, bgcolor: '#e3f2fd', borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary">
              An ICT engineer is remotely accessing your computer to assist you.
              You can send them a message here.
            </Typography>
          </Box>

          {/* Messages */}
          <Box
            sx={{
              height:        240,
              overflowY:     'auto',
              p:             1.5,
              display:       'flex',
              flexDirection: 'column',
              gap:           1,
              bgcolor:       '#fafafa',
            }}
          >
            {messages.length === 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textAlign: 'center', mt: 4, display: 'block' }}
              >
                No messages yet. The engineer can send you messages here.
              </Typography>
            )}

            {messages.map((msg, idx) => {
              const isEngineer = msg.senderRole === 'engineer';
              const senderInitial = isEngineer
                ? (msg.senderId?.name?.[0] || 'E')
                : (user?.name?.[0] || 'U');

              return (
                <Box
                  key={msg._id || idx}
                  sx={{
                    display:       'flex',
                    flexDirection: isEngineer ? 'row' : 'row-reverse',
                    gap:           1,
                    alignItems:    'flex-end',
                  }}
                >
                  {isEngineer && (
                    <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.main', fontSize: 11, flexShrink: 0 }}>
                      {senderInitial}
                    </Avatar>
                  )}
                  <Box
                    sx={{
                      maxWidth:     '75%',
                      px:           1.5,
                      py:           0.75,
                      borderRadius: isEngineer
                        ? '12px 12px 12px 4px'
                        : '12px 12px 4px 12px',
                      bgcolor: isEngineer ? 'primary.main' : 'grey.200',
                      color:   isEngineer ? 'white'          : 'text.primary',
                    }}
                  >
                    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                      {msg.message}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ opacity: 0.7, display: 'block', mt: 0.25, textAlign: 'right', fontSize: 10 }}
                    >
                      {new Date(msg.timestamp).toLocaleTimeString([], {
                        hour: '2-digit', minute: '2-digit'
                      })}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
            <div ref={messagesEndRef} />
          </Box>

          {/* Input */}
          <Box sx={{ p: 1.5, display: 'flex', gap: 1, borderTop: '1px solid', borderColor: 'divider' }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Type a message..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value.slice(0, 500))}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              multiline
              maxRows={3}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <Tooltip title="Send (Enter)">
              <span>
                <IconButton
                  color="primary"
                  onClick={handleSend}
                  disabled={!newMessage.trim() || sending}
                  sx={{ alignSelf: 'flex-end' }}
                >
                  <Send fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Collapse>
      </Paper>
    </Box>
  );
}
